import { useCallback, useEffect, useState } from 'react';
import { genesisApi, type GenesisGroup, type GenesisProposal } from '@/api/genesis';

export interface GenesisProposals {
  /** The `PROPOSED` ones of the kind asked for; empty until read, and after a failed read. */
  proposals: GenesisProposal[];
  /** The page of payee groups served with them (an envelope's top payees read their names). */
  groups: GenesisGroup[];
  /**
   * `loading` until the first read settles; `error` when it failed (a later failed re-read keeps
   * `ready`). The page's Items to review count reads it (TMMAPP-0159 §F 27).
   */
  status: 'loading' | 'ready' | 'error';
  reload: () => Promise<void>;
}

/**
 * The history's proposals for one section (TMMAPP-0141 §B): Planned's recurring bills or a virtual
 * account's envelopes, from `GET /v1/me/genesis?limit=1` (the limit pages payee groups, not
 * proposals). A failed read shows nothing: the section is a suggestion, never a blocker. Nothing is
 * read while `enabled` is false (proposals are the subscriber's own).
 */
export function useGenesisProposals(
  kind: 'series' | 'envelopes',
  enabled: boolean,
): GenesisProposals {
  const [proposals, setProposals] = useState<GenesisProposal[]>([]);
  const [groups, setGroups] = useState<GenesisGroup[]>([]);
  const [status, setStatus] = useState<GenesisProposals['status']>('loading');

  const reload = useCallback(async () => {
    if (!enabled) return;
    try {
      const genesis = await genesisApi.get({ limit: 1 });
      setProposals(genesis.proposals[kind].filter((p) => p.status === 'PROPOSED'));
      setGroups(genesis.payeeGroups);
      setStatus('ready');
    } catch {
      // A failed re-read keeps what is shown; a failed first read shows nothing.
      setStatus((prev) => (prev === 'ready' ? prev : 'error'));
    }
  }, [enabled, kind]);

  useEffect(() => {
    queueMicrotask(() => {
      void reload();
    });
  }, [reload]);

  return { proposals, groups, status, reload };
}
